import { DeckLayer } from '@deck.gl/arcgis';
import { GoogleMapsOverlay as DeckOverlay } from '@deck.gl/google-maps';
import { DEFAULT_MAP_SOURCE } from '@env';
import { Observable, Subject, Subscription } from 'rxjs';

export class FsDeckGLOverlay {
  public overlay: DeckLayer | DeckOverlay;
  public tooltip: string;
  public subscriptions: Map<string, Subscription> = new Map();

  private readonly _newClick$: Subject<any> = new Subject();
  private readonly _newRightClick$: Subject<any> = new Subject();
  private readonly _newHover$: Subject<any> = new Subject();
  private readonly _newDrag$: Subject<any> = new Subject();

  get newClick$(): Observable<any> {
    return this._newClick$.asObservable();
  }

  get newRightClick$(): Observable<any> {
    return this._newRightClick$.asObservable();
  }

  get newHover$(): Observable<any> {
    return this._newHover$.asObservable();
  }

  get newDrag$(): Observable<any> {
    return this._newDrag$.asObservable();
  }

  get defaultMapSource(): string {
    return DEFAULT_MAP_SOURCE;
  }

  public get viewports(): any {
    if (this.defaultMapSource === 'google') {
      return this.overlay?.deck?.viewManager?.viewports;
    } else if (this.defaultMapSource === 'esri') {
      return this.overlay?.deck?.viewManager?.viewports;
    }
    return null;
  }

  constructor() {
    switch (this.defaultMapSource) {
      case 'esri':
        this.overlay = this._handleEsriLayer();
        break;
      case 'google':
        this.overlay = this._handleGoogleLayer();
        break;
    }
  }

  public updateMap(): this {
    switch (this.defaultMapSource) {
      case 'esri':
        this.overlay = this._handleEsriLayer();
        break;
      case 'google':
        this.overlay = this._handleGoogleLayer();
        break;
    }

    return this;
  }

  public setTooltip(value: string): void {
    this.tooltip = value;
  }

  public addLayer(layer: any, index?: number): DeckLayer | DeckOverlay {
    if (this.defaultMapSource === 'google') {
      const layers = this.overlay.props.layers.filter((lay) => lay);
      if (index !== undefined && index >= 0 && index < layers.length) {
        layers.splice(index, 0, layer);
      } else {
        layers.push(layer);
      }

      this.overlay.setProps({
        layers,
      });
    } else {
      const layers = this.overlay.deck.layers.filter((lay) => lay);
      if (index !== undefined && index >= 0 && index < layers.length) {
        layers.splice(index, 0, layer);
      } else {
        layers.unshift(layer);
      }
      this.overlay.deck.set({
        layers,
      });
    }

    return this.overlay;
  }

  public removeLayer(id: string): DeckLayer | DeckOverlay {
    if (this.defaultMapSource === 'google') {
      const layers = this.overlay.props.layers;
      const index = layers.findIndex((layer) => {
        return layer?.id === id;
      });
      layers.splice(index, 1);
      this.overlay.setProps({
        layers: [...layers],
      });
    } else {
      const layers = this.overlay.deck.layers;
      const index = layers.findIndex((layer) => {
        return layer?.id === id;
      });
      layers.splice(index, 1);
      this.overlay.deck.layers = [...layers];
    }

    return this.overlay;
  }

  public toggleLayer(layer: any, newProps: any): void {
    if (this.defaultMapSource === 'google') {
      this.overlay.props.layers.filter((layer) => layer);
      const layers = this.overlay.props.layers;
      const index = layers.findIndex((lay) => {
        return lay?.id === layer.id;
      });

      if (index === -1) {
        return;
      }
      this.overlay.props.layers[index] = layers[index].clone(newProps);
    } else {
      const layers = this.overlay.deck.layers;
      const index = layers.findIndex((lay) => {
        return lay?.id === layer.id;
      });

      if (index === -1) {
        return;
      }
      this.overlay.deck.layers[index] = layers[index].clone(newProps);
    }
  }

  public resetLayers(): void {
    if (this.defaultMapSource === 'google') {
      this.overlay.setProps({
        layers: [],
      });
    } else {
      this.overlay.deck.layers = [];
    }
  }

  public destroyOverlay(): void {
    if (this.defaultMapSource === 'google') {
      this.overlay.finalize();
    } else {
      this.overlay.destroy();
    }

    this.overlay = null;
  }

  public addSubscription(id: string, subscription: Subscription): void {
    if (this.subscriptions.has(id)) {
      this.subscriptions.get(id)?.unsubscribe();
    }
    this.subscriptions.set(id, subscription);
  }

  public unsubscribe(id?: string): void {
    if (id) {
      this.subscriptions.get(id)?.unsubscribe();
      this.subscriptions.delete(id);
    } else {
      this.subscriptions.forEach((subscription) => subscription.unsubscribe());
      this.subscriptions.clear();
    }
  }

  private _handleEsriLayer(): DeckLayer {
    return new DeckLayer({
      'deck.layers': [],
      'deck.onClick': this._onClick.bind(this),
      'deck.onHover': this._onHover.bind(this),
      'deck.getTooltip': (info) => this._getTooltip(info),
    });
  }

  private _handleGoogleLayer(): DeckOverlay {
    return new DeckOverlay({
      layers: [],
      onClick: this._onClick.bind(this),
      onHover: this._onHover.bind(this),
      getTooltip: (info) => this._getTooltip(info),
    });
  }

  private _onClick(object: any, event: any): void {
    if (event.srcEvent.button === 0 || event.srcEvent.domEvent?.button === 0) {
      this._newClick$.next(object);
    } else if (event.srcEvent.button === 2 || event.srcEvent.domEvent?.button === 2) {
      this._newRightClick$.next(object);
    }
  }

  private _onHover(object: any): void {
    this._newHover$.next(object);
  }

  private _getTooltip(info: any): any {
    if (info.layer) {
      let value;
      if (info.layer.props.getTooltip || info.layer.data.getTooltip) {
        typeof info.layer.props?.getTooltip !== 'undefined'
          ? (value = info.layer.props?.getTooltip())
          : (value = info.layer.data?.getTooltip());
      }
      if (value === null || value === undefined) {
        return;
      }
    }
    return (
      info.object &&
      `\
    ${info.object.properties?.[this.tooltip] || info.object?.[this.tooltip]}`
    );
  }
}
